import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { 
  Play, 
  Share2, 
  Download, 
  Maximize, 
  Volume2, 
  VolumeX,
  Users,
  Check,
  Copy,
  Trophy,
  Mic,
  Sparkles
} from "lucide-react";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useVoiceAnnouncer } from "@/components/VoiceAnnouncer";
import confetti from "canvas-confetti";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DrawCeremonyProps {
  minValue: number;
  maxValue: number;
  onComplete?: (result: number) => void;
}

export const DrawCeremony = ({ minValue, maxValue, onComplete }: DrawCeremonyProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [announcerStyle, setAnnouncerStyle] = useState<"gameshow" | "casino" | "party" | "professional">("gameshow");
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [result, setResult] = useState<number | null>(null);
  const [drawId, setDrawId] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [viewerCount, setViewerCount] = useState(1);
  const [showBalloons, setShowBalloons] = useState(false);
  
  const announcer = useVoiceAnnouncer({ style: announcerStyle });

  // Real-time viewer count
  useEffect(() => {
    if (!drawId) return;

    const channel = supabase
      .channel(`draw-${drawId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'draws',
          filter: `id=eq.${drawId}`
        },
        (payload) => {
          if (payload.new.viewer_count) {
            setViewerCount(payload.new.viewer_count);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [drawId]);

  const fireConfetti = () => {
    const duration = 3000;
    const end = Date.now() + duration;

    const colors = ['#0066CC', '#00CCCC', '#FFD700'];

    (function frame() {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    })();
  };

  const playSound = (type: 'countdown' | 'reveal') => {
    if (!soundEnabled) return;
    
    // Using Web Audio API for simple beeps
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    if (type === 'countdown') {
      oscillator.frequency.value = 400;
      gainNode.gain.value = 0.3;
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.1);
    } else {
      oscillator.frequency.value = 800;
      gainNode.gain.value = 0.5;
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.3);
    }
  };

  const startCeremony = async () => {
    setIsDrawing(true);
    
    // Voice announcement for 3
    if (voiceEnabled) {
      await announcer.announceThree();
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    // Start countdown
    for (let i = 3; i > 0; i--) {
      setCountdown(i);
      playSound('countdown');
      
      // Announce at 2
      if (i === 2 && voiceEnabled) {
        await announcer.announceTwo();
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    setCountdown(null);
    
    // Generate random number using crypto API
    const randomBuffer = new Uint32Array(1);
    crypto.getRandomValues(randomBuffer);
    const range = maxValue - minValue + 1;
    const randomValue = randomBuffer[0] / (0xffffffff + 1);
    const winningNumber = Math.floor(randomValue * range) + minValue;
    
    // Create verification hash
    const timestamp = new Date().toISOString();
    const dataToHash = `${winningNumber}-${minValue}-${maxValue}-${timestamp}`;
    const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(dataToHash));
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const verificationHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    // Generate share URL
    const { data: shareData } = await supabase.rpc('generate_share_url');
    const shareCode = shareData as string;
    
    // Save to database
    const { data: drawData, error } = await supabase
      .from('draws')
      .insert({
        draw_title: `Draw ${minValue}-${maxValue}`,
        min_value: minValue,
        max_value: maxValue,
        winning_number: winningNumber,
        verification_hash: verificationHash,
        share_url: shareCode,
        status: 'revealed',
        viewer_count: 1
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error saving draw:', error);
      toast.error('Failed to save draw');
    } else {
      setDrawId(drawData.id);
      setShareUrl(`${window.location.origin}/draw/${shareCode}`);
    }
    
    // Balloon pop effect before reveal
    setShowBalloons(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Reveal with animation
    setResult(winningNumber);
    setShowBalloons(false);
    playSound('reveal');
    fireConfetti();
    
    // Voice announcement of winner
    if (voiceEnabled) {
      await announcer.announceWinner(winningNumber);
    }
    
    setIsDrawing(false);
    
    if (onComplete) {
      onComplete(winningNumber);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  const copyShareLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Share link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadCertificate = async () => {
    if (!result || !drawId) return;

    // Create certificate HTML
    const certificateHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body {
              font-family: Arial, sans-serif;
              max-width: 800px;
              margin: 40px auto;
              padding: 40px;
              border: 3px solid #0066CC;
              background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
            }
            h1 { color: #0066CC; text-align: center; margin-bottom: 30px; }
            .winner { font-size: 72px; color: #00CCCC; text-align: center; font-weight: bold; margin: 30px 0; }
            .details { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .detail-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee; }
            .label { font-weight: bold; color: #666; }
            .badge { display: inline-block; background: #0066CC; color: white; padding: 4px 12px; border-radius: 12px; font-size: 12px; }
          </style>
        </head>
        <body>
          <h1>🏆 Official Draw Certificate 🏆</h1>
          <div class="winner">#${result}</div>
          <div class="details">
            <div class="detail-row">
              <span class="label">Draw Range:</span>
              <span>${minValue} - ${maxValue}</span>
            </div>
            <div class="detail-row">
              <span class="label">Timestamp:</span>
              <span>${new Date().toLocaleString()}</span>
            </div>
            <div class="detail-row">
              <span class="label">Draw ID:</span>
              <span style="font-family: monospace;">${drawId.substring(0, 8)}</span>
            </div>
            <div class="detail-row">
              <span class="label">Share URL:</span>
              <span>${shareUrl || 'N/A'}</span>
            </div>
          </div>
          <p style="text-align: center; color: #666; font-size: 12px; margin-top: 30px;">
            <span class="badge">Cryptographically Secure</span><br><br>
            Generated by RandomGen (numbers-generator.com)<br>
            This certificate serves as proof of a fair, unbiased random draw.
          </p>
        </body>
      </html>
    `;

    // Create and download
    const blob = new Blob([certificateHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `draw-certificate-${result}.html`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Certificate downloaded!');
  };

  return (
    <Card className="p-6">
      <div className="space-y-6">
        {/* Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
          <div>
            <Label className="text-sm mb-2 block">Announcer Style</Label>
            <Select value={announcerStyle} onValueChange={(value: any) => setAnnouncerStyle(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gameshow">
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Game Show (Epic & Dramatic)
                  </span>
                </SelectItem>
                <SelectItem value="casino">Casino Style (Smooth)</SelectItem>
                <SelectItem value="party">Party Mode (Fun & Loud)</SelectItem>
                <SelectItem value="professional">Professional (Clean)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setVoiceEnabled(!voiceEnabled)}
              className="flex-1"
            >
              <Mic className="w-4 h-4 mr-2" />
              {voiceEnabled ? "Voice On" : "Voice Off"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSoundEnabled(!soundEnabled)}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFullscreen}
            >
              <Maximize className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          
          {result && (
            <Badge variant="outline" className="gap-1">
              <Users className="w-3 h-3" />
              {viewerCount} watching
            </Badge>
          )}
        </div>

        {/* Main Display */}
        <div className="min-h-[300px] flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg p-8 relative overflow-hidden">
          {/* Balloon effect */}
          {showBalloons && (
            <div className="absolute inset-0 flex items-center justify-center gap-4 animate-scale-in">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="text-8xl animate-bounce"
                  style={{
                    animationDelay: `${i * 100}ms`,
                    animationDuration: '500ms'
                  }}
                >
                  🎈
                </div>
              ))}
            </div>
          )}
          
          {countdown !== null && !showBalloons && (
            <div className="text-9xl font-bold text-primary animate-scale-in">
              {countdown}
            </div>
          )}
          
          {isDrawing && countdown === null && (
            <div className="text-4xl font-semibold text-muted-foreground animate-pulse">
              Drawing...
            </div>
          )}
          
          {result !== null && (
            <div className="text-center space-y-4 animate-scale-in">
              <Trophy className="w-16 h-16 text-accent mx-auto" />
              <h2 className="text-2xl font-semibold text-muted-foreground">
                And the winner is...
              </h2>
              <div className="text-9xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                #{result}
              </div>
              <Badge className="text-lg px-6 py-2 bg-accent/10 text-accent border-accent/20">
                Verified Draw
              </Badge>
            </div>
          )}
          
          {!result && countdown === null && !isDrawing && (
            <div className="text-center space-y-4">
              <h3 className="text-2xl font-semibold text-foreground">
                Ready to Draw?
              </h3>
              <p className="text-muted-foreground">
                Range: {minValue} - {maxValue}
              </p>
              <Button
                size="lg"
                onClick={startCeremony}
                className="gap-2 text-lg px-8 py-6"
              >
                <Play className="w-5 h-5" />
                Start Live Draw
              </Button>
            </div>
          )}
        </div>

        {/* Actions */}
        {result && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="gap-2"
              onClick={copyShareLink}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Share Link
                </>
              )}
            </Button>
            
            <Button
              variant="outline"
              className="gap-2"
              onClick={downloadCertificate}
            >
              <Download className="w-4 h-4" />
              Download Certificate
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};
